import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader } from "@/components/common/loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listClientDocuments } from "@/features/documents/api";
import type { ClientDocument } from "@/features/documents/types";
import { ApiError } from "@/lib/api";
import { formatShortDate } from "@/lib/format";
import { useActiveClient } from "@/layouts/client-layout";

function fileTypeLabel(doc: ClientDocument): string {
  const ext = doc.name.includes(".") ? doc.name.split(".").pop() : "";
  return (ext || doc.contentType.split("/").pop() || "FILE").toUpperCase();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const COL_COUNT = 4;

export default function LastYearDocsPage() {
  const { client } = useActiveClient();
  // Recomputed per mount (not at module-eval) so it stays correct across a
  // year rollover; stable within a mount for the load() dependency.
  const prevYear = useMemo(() => new Date().getFullYear() - 1, []);
  const [docs, setDocs] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    // Reuse the documents list endpoint with the backend `year` filter.
    listClientDocuments(client.id, { year: prevYear })
      .then((rows) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setDocs(rows);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setDocs([]);
        setError(err instanceof ApiError ? err.message : "Failed to load documents.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [client.id, prevYear]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 text-3xl font-bold">Last Year Docs</h1>
        <p className="text-muted-foreground max-w-2xl">Documents from {prevYear}.</p>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Document Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="py-10">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-destructive text-center">
                  {error}
                </TableCell>
              </TableRow>
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-muted-foreground text-center">
                  No documents from {prevYear}.
                </TableCell>
              </TableRow>
            ) : (
              docs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell
                    className="text-foreground max-w-[280px] truncate font-medium"
                    title={doc.name}
                  >
                    {doc.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fileTypeLabel(doc)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatBytes(doc.sizeBytes)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatShortDate(doc.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
